import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext, Store } from '@ngxs/store';
import { cloneDeep } from 'src/ep-utils';
import { StateHelperService } from '../../../services/proprietary/state-helper.service';
import { ContractObjectDefaults } from './contract-object.defaults';
import { ArticleNodeType } from './contract-object.types';
import { AddLinePointer, RemoveLinePointer } from './contract.actions';
import {
    AddContractDetailItem,
    AddContractPartyItem,
    AddFactorPointer,
    AddFormPointer,
    AddLine,
    DeleteLine,
    RemoveContractDetailItem,
    RemoveContractPartyItem,
    RemoveFactorPointer,
    RemoveFormPointer,
    SetPrimaryLine,
    UpdateLine
} from './line.actions';

export interface LineStateModel {
    line: any;
    primaryLine: any;
}

@State<LineStateModel>({
    name: 'lineState',
    defaults: {
        line: {},
        primaryLine: {}
    }
})
@Injectable()
export class LineState {
    constructor(private helper: StateHelperService, public store: Store) { }

    //******* SELECTORS *********

    @Selector()
    static lineByLineID(state: LineStateModel) {
        return (lineId: string) => {
            let line: any = {};
            for (var i in state.line) {
                if (state.line[i].Line_ID == lineId) {
                    line = state.line[i];
                    break;
                }
            }
            return line || {};
        };
    }

    @Selector()
    static lineCount(state: LineStateModel) {
        let count = 0;
        for (var i in state.line) {
            count++;
        }
        return count;
    }

    @Selector()
    static lines(state: LineStateModel) {
        let result: any = [];

        for (var i in state.line) {
            result.push(state.line[i]);
        }

        return result;
    }

    @Selector()
    static primaryLine(state: LineStateModel) {
        return state.primaryLine || {};
    }

    @Selector()
    static lineCodeSet(state: LineStateModel) {
        let result: any = [];

        for (var i in state.line) {
            result.push({ Code: state.line[i].Line_Code, Code_Desc: state.line[i].Line_Code_Desc });
        }

        return result || [];
    }

    //******  ACTIONS  ******

    @Action(AddLine)
    addLine(sc: StateContext<LineStateModel>, action: AddLine) {
        const state = sc.getState();

        let lineID = this.helper.getTempID();
        let newLine: any = this.helper.createDefaultItem(ContractObjectDefaults, 'Contract_Line', action.line);
        newLine.Line_ID = lineID;
        this.helper.setItemAdded(newLine);

        sc.patchState({
            line: {
                ...state.line,
                [lineID]: newLine
            }
        });

        sc.dispatch([new AddLinePointer(lineID)]);
    }

    @Action(SetPrimaryLine)
    setPrimaryLine(sc: StateContext<LineStateModel>, action: SetPrimaryLine) {
        sc.patchState({
            primaryLine: action.primary
        });
    }

    @Action(DeleteLine)
    deleteLine(sc: StateContext<LineStateModel>, action: DeleteLine) {
        const state = sc.getState();
        let line = { ...state.line }; //shallow copy line from state

        this.helper.setItemDeleted(line, action.lineID);
        sc.patchState({ line: line });
        if (action.lineID < 0) {
            sc.dispatch([new RemoveLinePointer(action.lineID.toString())]);
        }
    }

    @Action(UpdateLine)
    UpdateLine(sc: StateContext<LineStateModel>, action: UpdateLine) {
        const state = sc.getState();
        let lineID = action.line.Line_ID;

        if (!lineID) {
            throw 'Line_ID must be included for a Line Update!';
        }

        let currentLine = state.line[lineID];

        let updateLine = cloneDeep(action.line);
        this.helper.setItemChanged(updateLine, currentLine.Edit_Status);

        sc.patchState({
            line: {
                ...state.line,
                [lineID]: {
                    ...state.line[lineID],
                    ...updateLine
                }
            }
        });
    }

    @Action(AddContractDetailItem)
    AddContractDetailItem(sc: StateContext<LineStateModel>, action: AddContractDetailItem) {
        // adds the article ID to the list of article ids within the article type
        // Line
        //      Contract_Detail
        //              CDAA_Vehicle [3333333,44444444,5555555]
        //

        const state = sc.getState();
        let lineID = action.lineID;

        if (!lineID) {
            throw 'Line_ID must be included to add a Contract Detail Item!';
        }

        let contractDetail = state.line[lineID].Contract_Detail ? { ...state.line[lineID].Contract_Detail } : {};
        let lineWideProduct: any[] = [];
        if (state.line[lineID].Line_Wide_Product) {
            lineWideProduct = [...state.line[lineID].Line_Wide_Product];
        }

        if (action.articleNodeType != ArticleNodeType.Line_Wide) {
            if (!contractDetail[action.articleNodeType]) {
                contractDetail[action.articleNodeType] = []; //if article node does not exist under Contract_Detail then create as empty array
            }
        }

        if (action.articleNodeType == ArticleNodeType.Line_Wide) {
            lineWideProduct = [...lineWideProduct, action.articleID];
        } else {
            contractDetail[action.articleNodeType] = [...contractDetail[action.articleNodeType], action.articleID];
        }

        sc.patchState({
            line: {
                ...state.line,
                [lineID]: {
                    ...state.line[lineID],
                    Contract_Detail: contractDetail,
                    Line_Wide_Product: lineWideProduct
                }
            }
        });
    }

    @Action(RemoveContractDetailItem)
    RemoveContractDetailItem(sc: StateContext<LineStateModel>, action: RemoveContractDetailItem) {
        // removes the article ID to the list of article ids within the article type
        // Line
        //      Contract_Detail
        //              CDAA_Vehicle [3333333,44444444,5555555]
        //

        const state = sc.getState();
        let lineID = action.lineID;

        if (!lineID) {
            throw 'Line_ID must be included to remove a Contract Detail Item!';
        }

        let contractDetail = state.line[lineID].Contract_Detail ? { ...state.line[lineID].Contract_Detail } : {};
        let lineWideProduct: any[] = [];
        if (state.line[lineID].Line_Wide_Product) {
            lineWideProduct = [...state.line[lineID].Line_Wide_Product];
        }

        if (action.articleNodeType == ArticleNodeType.Line_Wide) {
            lineWideProduct = this.helper.removeArrayItemByValue(lineWideProduct, action.articleID);
        } else {
            contractDetail[action.articleNodeType] = this.helper.removeArrayItemByValue(
                contractDetail[action.articleNodeType],
                action.articleID
            );
        }

        sc.patchState({
            line: {
                ...state.line,
                [lineID]: {
                    ...state.line[lineID],
                    Contract_Detail: contractDetail,
                    Line_Wide_Product: lineWideProduct
                }
            }
        });
    }

    @Action(AddContractPartyItem)
    AddContractPartyItem(sc: StateContext<LineStateModel>, action: AddContractPartyItem) {
        const state = sc.getState();

        let lineID = action.lineID;

        if (!lineID) {
            throw 'Line_ID must be included to add a Contract Party Item!';
        }

        let contractParty = { ...state.line[lineID].Contract_Party };

        if (!contractParty[action.partyNodeType]) {
            contractParty[action.partyNodeType] = [];
        }

        contractParty[action.partyNodeType] = [...contractParty[action.partyNodeType], action.partyID];

        sc.patchState({
            line: {
                ...state.line,
                [lineID]: {
                    ...state.line[lineID],
                    Contract_Party: contractParty
                }
            }
        });
    }

    @Action(RemoveContractPartyItem)
    RemoveContractPartyItem(sc: StateContext<LineStateModel>, action: RemoveContractPartyItem) {
        const state = sc.getState();

        let lineID = action.lineID;

        if (!lineID) {
            throw 'Line_ID must be included to remove a Contract Party Item!';
        }

        let contractParty = { ...state.line[lineID].Contract_Party };

        if (!contractParty[action.partyNodeType]) {
            contractParty[action.partyNodeType] = [];
        }

        contractParty[action.partyNodeType] = this.helper.removeArrayItemByValue(
            contractParty[action.partyNodeType],
            action.partyID
        );

        sc.patchState({
            line: {
                ...state.line,
                [lineID]: {
                    ...state.line[lineID],
                    Contract_Party: contractParty
                }
            }
        });
    }

    @Action(AddFormPointer)
    AddFormPointer(sc: StateContext<LineStateModel>, action: AddFormPointer) {
        const state = sc.getState();

        let lineID = action.lineID;

        if (!lineID) {
            throw 'Line_ID must be included to add a Contract Form!';
        }

        let contractLine = {
            ...state.line[lineID]
        };

        if (!contractLine.Contract_Form) {
            contractLine.Contract_Form = [];
        }

        contractLine.Contract_Form = [...contractLine.Contract_Form, action.articleID];

        sc.patchState({
            line: {
                ...state.line,
                [lineID]: contractLine
            }
        });
    }

    @Action(RemoveFormPointer)
    RemoveFormPointer(sc: StateContext<LineStateModel>, action: RemoveFormPointer) {
        const state = sc.getState();

        let lineID = action.lineID;

        if (!lineID) {
            throw 'Line_ID must be included to remove a Contract Form!';
        }

        let contractLine = {
            ...state.line[lineID]
        };

        contractLine.Contract_Form = this.helper.removeArrayItemByValue(contractLine.Contract_Form, action.articleID);

        sc.patchState({
            line: {
                ...state.line,
                [lineID]: contractLine
            }
        });
    }

    @Action(AddFactorPointer)
    AddFactorPointer(sc: StateContext<LineStateModel>, action: AddFactorPointer) {
        const state = sc.getState();
        let lineID = action.lineID;

        if (!lineID) {
            throw 'Line_ID must be included to add a Contract Factor!';
        }

        let contractLine = {
            ...state.line[lineID]
        };

        if (!contractLine.Contract_Factor) {
            contractLine.Contract_Factor = [];
        }

        contractLine.Contract_Factor = [...contractLine.Contract_Factor, action.factorID];

        sc.patchState({
            line: {
                ...state.line,
                [lineID]: contractLine
            }
        });
    }

    @Action(RemoveFactorPointer)
    RemoveFactorPointer(sc: StateContext<LineStateModel>, action: RemoveFactorPointer) {
        const state = sc.getState();
        let lineID = action.lineID;

        if (!lineID) {
            throw 'Line_ID must be included to remove a Contract Factor!';
        }

        let contractLine = {
            ...state.line[lineID]
        };

        contractLine.Contract_Factor = this.helper.removeArrayItemByValue(contractLine.Contract_Factor, action.factorID);

        sc.patchState({
            line: {
                ...state.line,
                [lineID]: contractLine
            }
        });
    }
}
